import { 
  KeyRound, 
  Lock, 
  ShieldCheck, 
  Activity, 
  ShieldBan, 
  FileCheck 
} from "lucide-react";

const stages = [
  {
    number: 1,
    icon: KeyRound,
    title: "Authentication",
    descriptions: [
      "Verify the agent's unique identity.",
      "No shared credentials, no ambiguity."
    ],
    gradient: "from-emerald-500 to-teal-600"
  },
  {
    number: 2,
    icon: Lock,
    title: "Authorization",
    descriptions: [
      "Every authorization traces to a human owner.",
      "Business owners define precisely what each agent can and cannot do, grounded in actual workflows."
    ],
    gradient: "from-teal-500 to-cyan-600"
  },
  {
    number: 3,
    icon: ShieldCheck,
    title: "Intent Validation",
    descriptions: [
      "Pre-execution validation of the agent's plan and intent.",
      "Governs every proposed action matches its approved business purpose before anything runs."
    ],
    gradient: "from-cyan-500 to-blue-600"
  },
  {
    number: 4,
    icon: Activity,
    title: "Intervention",
    descriptions: [
      "Human-in-the-loop escalation for high-risk or edge-case decisions."
    ],
    gradient: "from-blue-500 to-indigo-600"
  },
  {
    number: 5,
    icon: ShieldBan,
    title: "Enforcement",
    descriptions: [
      "Real-time blocking of any unauthorized, drifted, or misaligned behavior."
    ],
    gradient: "from-indigo-500 to-violet-600"
  },
  {
    number: 6,
    icon: FileCheck,
    title: "Audit",
    descriptions: [
      "Immutable, deterministic logs prove compliance.",
      "Every action traceable, no probabilistic guesswork."
    ],
    gradient: "from-violet-500 to-purple-600"
  }
];

export const GovernanceStages = () => {
  return (
    <section 
      id="governance" 
      className="py-20 sm:py-28 lg:py-36 px-6 lg:px-10 bg-slate-950/50 relative overflow-hidden"
      data-testid="governance-section"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-[0.02]">
        <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="hexagons" width="56" height="100" patternUnits="userSpaceOnUse" patternTransform="scale(2)">
              <path d="M28 66L0 50V18L28 2L56 18V50L28 66Z" fill="none" stroke="currentColor" strokeWidth="0.5" className="text-violet-500" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#hexagons)" />
        </svg>
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-12 lg:mb-20 reveal">
          <span 
            className="text-[9px] sm:text-[10px] font-extrabold text-violet-500 uppercase tracking-[0.4em] sm:tracking-[0.6em] mb-4 block"
            data-testid="governance-label"
          >
            How It Works
          </span>
          <h2 
            className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold text-white tracking-tighter mb-6"
            data-testid="governance-headline"
          >
            6 Stages to True Entitlement Governance
          </h2>
          <p className="text-base lg:text-lg text-slate-400 max-w-3xl mx-auto leading-relaxed">
            ElixirID doesn't just monitor AI agents, it governs them and what they do. From authentication to business authorization, enforcement, and audit, every agent action is scoped, tracked, and traceable to a human owner.
          </p>
        </div>

        {/* Stages Grid */}
        <div 
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
          data-testid="governance-stages"
        >
          {stages.map((stage, index) => (
            <div 
              key={index}
              className="group relative flex flex-col items-center text-center p-6 lg:p-8 rounded-2xl bg-slate-900/30 border border-slate-800 hover:border-violet-500/30 transition-all duration-500 hover:bg-slate-900/50"
              data-testid={`stage-card-${index}`}
            >
              {/* Hover Glow */}
              <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-[radial-gradient(circle_at_50%_0%,rgba(139,92,246,0.08),transparent_70%)]" />
              
              {/* Top Accent Line */}
              <div className="absolute -top-px left-1/2 -translate-x-1/2 w-16 h-px opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-r from-transparent via-violet-500/50 to-transparent" />
              
              {/* Icon */}
              <div className="relative mb-6">
                <div className={`w-14 h-14 lg:w-16 lg:h-16 rounded-2xl bg-gradient-to-br ${stage.gradient} flex items-center justify-center shadow-lg group-hover:shadow-xl transition-shadow`}>
                  <stage.icon className="w-7 h-7 lg:w-8 lg:h-8 text-white" />
                </div>
                <div className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-slate-900 border-2 border-slate-700 flex items-center justify-center shadow-sm">
                  <span className="text-[10px] font-bold text-slate-400">{stage.number}</span>
                </div>
              </div>

              {/* Content */}
              <div className="relative z-10">
                <h3 className="text-lg font-bold text-white mb-3">{stage.title}</h3>
                <div className="text-sm text-slate-400 leading-relaxed space-y-2">
                  {stage.descriptions.map((desc, i) => (
                    <p key={i}>{desc}</p>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
